import React, { useState } from 'react';
import { X, Calendar, Clock, AlertTriangle } from 'lucide-react';

interface EnableTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (endDate: string) => Promise<void>;
  businessName: string;
  isLoading: boolean;
}

export default function EnableTrialModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  businessName,
  isLoading 
}: EnableTrialModalProps) {
  const [trialEndDate, setTrialEndDate] = useState('');
  const [error, setError] = useState('');

  // Calculate minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  // Calculate default date (30 days from now)
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 30);
  const defaultDate = defaultEndDate.toISOString().split('T')[0];

  React.useEffect(() => {
    if (isOpen && !trialEndDate) {
      setTrialEndDate(defaultDate);
    }
  }, [isOpen, defaultDate, trialEndDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!trialEndDate) {
      setError('Please select a trial end date');
      return;
    }

    const endDate = new Date(trialEndDate);
    const now = new Date();

    if (endDate <= now) {
      setError('Trial end date must be in the future');
      return;
    }

    try {
      await onConfirm(trialEndDate);
      onClose();
      setTrialEndDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable trial');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setTrialEndDate('');
      setError('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Enable Trial</h3>
              <p className="text-sm text-gray-600">{businessName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Trial Activation</p>
                <p>
                  This will immediately activate the business account and all associated resources 
                  until the specified end date. The business will have full access to all features 
                  during the trial period.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="trialEndDate" className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-2" />
              Trial End Date
            </label>
            <input
              type="date"
              id="trialEndDate"
              value={trialEndDate}
              onChange={(e) => setTrialEndDate(e.target.value)}
              min={minDate}
              required
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              Trial will automatically expire at midnight on this date
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Enabling...
                </>
              ) : (
                'Enable Trial'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
